import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Wallet, TrendingUp, Sparkles, Activity, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { usePanelT } from "../../hooks/usePanelT";
import { FirstDepositGuide } from "../../components/panel/FirstDepositGuide";

function formatUsd(n) {
    return `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const tierColor = {
    Starter: "#A6A8AC",
    Pro: "#C9C3B6",
    Scale: "#2FA57C",
    Whale: "#2FA57C",
};

export default function PanelHome() {
    const { user } = useAuth();
    const { t } = usePanelT();
    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [accountsCount, setAccountsCount] = useState(null);

    useEffect(() => {
        api.get("/me/wallet").then(({ data }) => setWallet(data)).catch(() => {});
        api.get("/me/transactions").then(({ data }) => setTransactions(data.slice(0, 5))).catch(() => {});
        api.get("/me/notifications").then(({ data }) => setNotifications(data.slice(0, 5))).catch(() => {});
        api.get("/me/ad-accounts").then(({ data }) => setAccountsCount(data.filter((a) => !["cancelled", "rejected"].includes(a.status)).length)).catch(() => setAccountsCount(0));
    }, []);

    const accent = wallet ? tierColor[wallet.current_tier] || "#2FA57C" : "#2FA57C";

    return (
        <div data-testid="panel-home" className="space-y-6 md:space-y-8">
            <div>
                <h1 className="font-display font-bold text-2xl md:text-4xl tracking-tight">
                    {t("h.welcome")}, {user?.full_name?.split(" ")[0] || t("h.operator")}.
                </h1>
                <p className="text-muted-foreground mt-1 text-sm hidden sm:block">
                    {t("h.sub")}
                </p>
            </div>

            {accountsCount === 0 && <FirstDepositGuide wallet={wallet} accountsCount={accountsCount} />}

            {/* Top stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                <div data-testid="panel-stat-balance" className="bg-card border border-primary/30 rounded-2xl p-4 md:p-5 col-span-2 lg:col-span-1">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono uppercase tracking-wider mb-3">
                        <Wallet className="w-3.5 h-3.5" /> {t("walletBalance")}
                    </div>
                    <div className="font-display font-black text-2xl md:text-3xl tabular-nums">
                        {wallet ? formatUsd(wallet.usd_balance) : "—"}
                    </div>
                </div>
                <div data-testid="panel-stat-tier" className="bg-card border rounded-2xl p-4 md:p-5" style={{ borderColor: `${accent}55` }}>
                    <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono uppercase tracking-wider mb-3">
                        <TrendingUp className="w-3.5 h-3.5" /> {t("h.tier")}
                    </div>
                    <div className="font-display font-black text-2xl md:text-3xl" style={{ color: accent }}>
                        {wallet?.current_tier || "—"}
                    </div>
                </div>
                <div data-testid="panel-stat-cashback" className="bg-card border border-[#2FA57C]/25 rounded-2xl p-4 md:p-5">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono uppercase tracking-wider mb-3">
                        <Sparkles className="w-3.5 h-3.5 text-[#2FA57C]" /> {t("h.cashbackEarned")}
                    </div>
                    <div className="font-display font-black text-2xl md:text-3xl text-[#2FA57C] tabular-nums">
                        {wallet ? formatUsd(wallet.cashback_balance) : "—"}
                    </div>
                </div>
                <div data-testid="panel-stat-monthly" className="bg-card border border-white/[0.07] rounded-2xl p-4 md:p-5 col-span-2 sm:col-span-1">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono uppercase tracking-wider mb-3">
                        <Activity className="w-3.5 h-3.5" /> {t("w.monthly")}
                    </div>
                    <div className="font-display font-black text-2xl md:text-3xl tabular-nums">
                        {wallet ? formatUsd(wallet.monthly_spend) : "—"}
                    </div>
                </div>
            </div>

            {/* Tier progress */}
            {wallet?.next_tier && wallet.spend_to_next > 0 && (
                <div data-testid="panel-tier-progress" className="bg-card border border-white/[0.07] rounded-2xl p-6">
                    <div className="flex items-baseline justify-between mb-2">
                        <h3 className="font-display font-semibold text-lg">
                            <span style={{ color: accent }}>{wallet.current_tier}</span> →{" "}
                            <span className="text-muted-foreground">{wallet.next_tier}</span>
                        </h3>
                        <p className="text-sm font-mono text-muted-foreground">
                            {t("h.remaining")} {formatUsd(wallet.spend_to_next)}
                        </p>
                    </div>
                    <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
                        <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                                width: `${Math.round((wallet.progress_to_next || 0) * 100)}%`,
                                background: accent,
                            }}
                        />
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                        {t("h.tierNote")}
                    </p>
                </div>
            )}

            <div className="grid lg:grid-cols-2 gap-4 md:gap-6">
                <div className="bg-card border border-white/[0.07] rounded-2xl p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-lg">{t("h.recentTx")}</h3>
                        <Link to="/panel/wallet" className="text-xs text-primary hover:underline" data-testid="panel-link-wallet">
                            {t("h.openWallet")} →
                        </Link>
                    </div>
                    {transactions.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-6 text-center">{t("w.noTx")}</p>
                    ) : (
                        <ul className="space-y-3">
                            {transactions.map((tx) => (
                                <li key={tx.id} className="flex items-center justify-between text-sm">
                                    <div>
                                        <p className="font-medium">{t(`tx.${tx.type}`) === `tx.${tx.type}` ? tx.type.replace(/_/g, " ") : t(`tx.${tx.type}`)}</p>
                                        <p className="text-xs text-muted-foreground font-mono">
                                            {new Date(tx.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <span className={`font-display font-semibold ${(tx.type === "spend" || tx.type === "ad_account_topup" || Number(tx.amount) < 0) ? "text-foreground/85" : "text-[#00b67a]"}`}>
                                        {(tx.type === "spend" || tx.type === "ad_account_topup" || Number(tx.amount) < 0) ? "-" : "+"}{formatUsd(Math.abs(tx.amount))}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="bg-card border border-white/[0.07] rounded-2xl p-4 md:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-lg">{t("nb.title")}</h3>
                    </div>
                    {notifications.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-6 text-center">{t("h.noNotif")}</p>
                    ) : (
                        <ul className="space-y-3">
                            {notifications.map((n) => (
                                <li
                                    key={n.id}
                                    className={`text-sm pb-3 border-b border-white/5 last:border-0 ${n.read ? "" : "text-foreground"}`}
                                >
                                    <p className={`${n.read ? "" : "font-semibold"}`}>{n.title}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5">{n.body}</p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <div className="bg-card border border-primary/30 rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 glow-primary">
                <div>
                    <h3 className="font-display font-semibold text-lg">{t("h.needAccount")}</h3>
                    <p className="text-sm text-muted-foreground">
                        {t("h.needAccountSub")}
                    </p>
                </div>
                <Link to="/panel/ad-accounts" data-testid="panel-cta-request-account">
                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold w-full md:w-auto h-11">
                        <Plus className="w-4 h-4 mr-2" /> {t("acc.request")}
                    </Button>
                </Link>
            </div>
        </div>
    );
}
